<template>
  <div id="page-container">
    <div :class="containerClass" class="mt-4">
      <h2 class="text-center mb-2">Interviews</h2>
      <p class="text-center mb-4">
        Select from the list of interviews to get started!
      </p>
      <div class="page-section px-0" :class="{ jobs_container: get(getLoggedInUser, 'role') === USER_ROLES.STUDENT }">
        <div :class="{ container: get(getLoggedInUser, 'role') === USER_ROLES.STUDENT }">
          <div class="d-flex flex-column flex-sm-row align-items-sm-center mb-24pt" style="white-space: nowrap">
            <div
              v-if="get(getLoggedInUser, 'role_type') === USER_ROLES.STUDENT"
              class="w-auto table d-flex mb-sm-0"
              :class="{ 'flex-column': isMobSmallScreen }"
            >
              <form
                style="width: 100%"
                class="search-form search-form--light d-lg-inline-flex mb-8pt mb-lg-0"
                @submit.prevent=""
              >
                <b-form-input class="w-lg-auto" placeholder="Search..." v-model="searchTerm" @input="onSearch" />
                <b-btn variant="flush" type="submit">
                  <md-icon v-text="'search'" />
                </b-btn>
              </form>
            </div>
            <div
              v-if="get(getLoggedInUser, 'role_type') === USER_ROLES.STUDENT"
              class="w-auto ml-sm-auto mt-2 mt-sm-0"
            >
              <b-btn :to="{ name: 'student-submissions-listing' }" variant="primary">
                <md-icon class="mr-1">history</md-icon>My Interviews
              </b-btn>
            </div>
          </div>
          <div
            v-if="enableSidebarToggle !== false"
            class="w-auto ml-sm-auto mb-sm-0"
            style="display:flex;justify-self: end;"
          >
            <b-btn v-fmv-toggle.filters size="sm" variant="white" class="ml-auto">
              <md-icon left>tune</md-icon>
              <span>Filters</span>
            </b-btn>
          </div>

          <page-separator
            class="mb-4"
            :title="
              !isLoading
                ? `showing ${
                    totalSimulations ? `${pageFromCount}-${pageToCount}` : 0
                  } of ${totalSimulations} ${pageTitle || 'Interviews'}`
                : ''
            "
          />

          <div class="d-flex justify-content-center" v-if="!totalSimulations && !isLoading">
            <div class="card card-block card-stretch card-height col-md-8">
              <div class="card-body text-center">
                <i class="fas fa-exclamation-circle fa-lg text-muted mt-4 mb-4" style="font-size: 3rem" />
                <h4>No {{ pageTitle || 'Interviews' }} Found</h4>
                <p class="text-muted">
                  No {{ pageTitle.toLowerCase() || 'interviews' }} currently available. Please check again later.
                </p>
              </div>
            </div>
          </div>

          <b-skeleton-wrapper :loading="isLoading" v-else>
            <template #loading>
              <div class="row">
                <div class="col-md-6" v-for="item in [1, 2]" :key="item">
                  <div class="card">
                    <b-skeleton-img width="100%"></b-skeleton-img>

                    <div class="p-2">
                      <b-skeleton width="55%"></b-skeleton>
                      <b-skeleton width="70%"></b-skeleton>
                    </div>
                  </div>
                </div>
              </div>
            </template>
            <div class="row">
              <div class="col-md-6" v-for="simulation in simulations" :key="simulation.id">
                <router-link
                  :to="{
                    name: `student-simulation-details`,
                    params: {
                      id: simulation.slug,
                    },
                  }"
                >
                  <div class="card clickable-item" style="height: 95%;">
                    <img
                      class=""
                      style="object-fit: cover; border-radius: 8px"
                      :src="simulation.image_url ? simulation.image_url : DefaultAvatar"
                      width="100%"
                      height="230px"
                    />
                    <div class="d-flex align-items-center p-2">
                      <img
                        class="mx-2"
                        :src="
                          get(simulation, 'posted_by.logo_url') ? get(simulation, 'posted_by.logo_url') : DefaultAvatar
                        "
                        width="50"
                        height="auto"
                      />

                      <div class="d-flex flex-column justify-content-center">
                        <div class="card-title">{{ simulation.title }}</div>
                        <div class="">
                          {{ truncate(simulation.instructions, { length: 50 }) }}
                        </div>
                      </div>
                    </div>
                  </div>
                </router-link>
              </div>
            </div>
          </b-skeleton-wrapper>

          <pagination
            v-if="totalSimulations > simulations.length"
            v-model="currentPage"
            :total-rows="totalSimulations"
            :per-page="perPage"
            @change="onPageChange"
            aria-controls="simulations-table"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { debounce, get, truncate } from 'lodash';
import Pagination from '@/components/Ui/Pagination.vue';
import { mapActions, mapGetters } from 'vuex';
import DefaultAvatar from '@/assets/images/logos/logo_icon.png';
import { USER_ROLES } from '@/common/constants';
import Page from '@/components/Page.vue';
import { formatFullDate } from '@/common/utils';
import PageSeparator from '@/components/Ui/PageSeparator.vue';
import FiltersSidebar from './Filters/SimulationFiltersSidebar.vue';
import Filters from './Filters/SimulationFilters.vue';

export default {
  name: 'DashboardSimulations',
  components: { Pagination, PageSeparator },
  extends: Page,
  props: {
    pageTitle: { type: String, default: 'Interviews' },
  },

  data() {
    return {
      DefaultAvatar,
      title: 'Interviews',
      isLoading: true,
      perPage: 14,
      currentPage: 1,
      totalSimulations: 0,
      simulations: [],
      searchTerm: '',
      ordering: '-id',
      pageFromCount: 0,
      pageToCount: 0,
      windowWidth: window.innerWidth,
      USER_ROLES,
      onfirstLoad: true,
      filters: {
        program: null,
        university: null,
      },
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser', 'getLoggedInUserDashboard']),
    ...mapGetters('simulations', ['getSchoolAdmissionSimulationFilters']),

    isMobSmallScreen() {
      return this.windowWidth <= 776;
    },
    subLayout() {
      return this.windowWidth > 992;
    },
    subLayoutDrawer() {
      return this.windowWidth > 992 ? FiltersSidebar : Filters;
    },
    subLayoutDrawerId() {
      return 'filters';
    },

    enableSidebarToggle() {
      return this.windowWidth <= 992;
    },
  },

  methods: {
    ...mapActions('simulations', ['getSchoolAdmissionSimulations']),
    get,
    truncate,
    formatFullDate,

    async fetchSimulations(pageNum = 1, params = {}) {
      this.isLoading = true;

      try {
        const response = await this.getSchoolAdmissionSimulations({
          limit: this.perPage,
          offset: (pageNum - 1) * this.perPage,
          ...(this.ordering && { ordering: this.ordering }),
          ...(this.searchTerm && { search: this.searchTerm }),
          is_active: true,
          program: this.filters.program,
          university: this.filters.university,
          ...params,
        });

        this.simulations = response.data.results;
        this.currentPage = pageNum;
        this.totalSimulations = response.data.count;
        this.pageFromCount = (this.currentPage - 1) * this.perPage + 1;
        this.pageToCount = this.pageFromCount + get(response.data, 'results.length', 0) - 1;
      } catch (err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }

      this.isLoading = false;
    },

    onPageChange(pageNum) {
      this.fetchSimulations(pageNum);
    },

    onSortChange(context) {
      this.ordering = context.sortDesc ? '-' + context.sortBy : context.sortBy;
      this.fetchSimulations();
    },

    onSearch() {
      this.debouncedSearchSimulations(this);
    },

    debouncedSearchSimulations: debounce(vm => {
      vm.fetchSimulations();
    }, 500),

    handleResize() {
      this.windowWidth = window.innerWidth;
    },
  },
  watch: {
    getSchoolAdmissionSimulationFilters: {
      deep: true,
      handler(filterValues) {
        this.filters.program = filterValues.program;
        this.filters.university = filterValues.university;
        this.debouncedSearchSimulations(this);
        setTimeout(() => {
          this.onfirstLoad = false;
        }, 500);
      },
    },
  },
  async mounted() {
    window.addEventListener('resize', this.handleResize);
    await this.fetchSimulations();
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
};
</script>
